import { getAuth } from 'firebase/auth';
import {
  collection,
  deleteField,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  where,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { BaseCRUDManager } from './base/BaseCRUDManager';
import type {
  IWorkStreamTodo,
  WorkStreamTodoCreateData,
  WorkStreamTodoUpdateData,
} from '../types/WorkStreamTodo';

const COLLECTION = 'workStreamTodos';

export const WorkStreamTodoManager = {
  observeTodosForUser(
    organisationId: string,
    userKey: string,
    callback: (todos: IWorkStreamTodo[]) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    const q = query(
      collection(db, COLLECTION),
      where('organisationId', '==', organisationId),
      where('userKey', '==', userKey),
      orderBy('createdAt', 'desc')
    );

    return onSnapshot(
      q,
      (snap) => {
        const todos = snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        })) as IWorkStreamTodo[];
        callback(todos);
      },
      (error) => onError?.(error)
    );
  },

  async listTodosForUser(organisationId: string, userKey: string): Promise<IWorkStreamTodo[]> {
    return BaseCRUDManager.query<IWorkStreamTodo>(COLLECTION, {
      filters: [
        { field: 'organisationId', operator: '==', value: organisationId },
        { field: 'userKey', operator: '==', value: userKey },
      ],
      orderBy: [{ field: 'createdAt', direction: 'desc' }],
    });
  },

  async createTodo(data: WorkStreamTodoCreateData): Promise<string> {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated to create a to-do');
    }

    const title = data.title.trim();
    if (!title) {
      throw new Error('To-do title is required');
    }

    const now = Timestamp.now();
    const completed = Boolean(data.completed);
    const payload: Record<string, unknown> = {
      organisationId: data.organisationId,
      userKey: data.userKey,
      title,
      completed,
      source: data.source ?? 'manual',
      createdBy: currentUser.uid,
      createdAt: now,
      updatedAt: now,
    };

    if (completed) {
      payload.completedAt = now;
    }

    if (data.meetingId) payload.meetingId = data.meetingId;
    if (data.meetingTitle) payload.meetingTitle = data.meetingTitle;
    if (data.meetingActionId) payload.meetingActionId = data.meetingActionId;
    if (data.workStreamId) payload.workStreamId = data.workStreamId;
    if (data.contributionTaskId) payload.contributionTaskId = data.contributionTaskId;
    if (data.livingWageReservationId) {
      payload.livingWageReservationId = data.livingWageReservationId;
    }
    if (data.teamUpdatePostId) payload.teamUpdatePostId = data.teamUpdatePostId;
    if (data.teamUpdateGroupId) payload.teamUpdateGroupId = data.teamUpdateGroupId;

    return BaseCRUDManager.create(COLLECTION, payload as Omit<IWorkStreamTodo, 'id'>, {
      timestampMethod: 'client',
      autoTimestamps: false,
    });
  },

  async updateTodo(todoId: string, updates: WorkStreamTodoUpdateData): Promise<void> {
    const payload: Record<string, unknown> = {
      updatedAt: Timestamp.now(),
    };

    if (updates.title !== undefined) {
      payload.title = updates.title.trim();
    }

    if (updates.completed === true) {
      payload.completed = true;
      payload.completedAt = updates.completedAt ?? Timestamp.now();
    } else if (updates.completed === false) {
      payload.completed = false;
      payload.completedAt = deleteField();
    }

    await BaseCRUDManager.update(COLLECTION, todoId, payload, {
      timestampMethod: 'client',
      autoTimestamps: false,
    });
  },

  async deleteTodo(todoId: string): Promise<void> {
    return BaseCRUDManager.delete(COLLECTION, todoId);
  },

  /**
   * Mark incomplete living-wage mirrored to-dos complete when a reservation completes.
   */
  async completeMirroredTodosForReservation(params: {
    organisationId: string;
    reservationId: string;
    userKeys: string[];
  }): Promise<void> {
    const uniqueKeys = [...new Set(params.userKeys.filter(Boolean))];
    await Promise.all(
      uniqueKeys.map(async (userKey) => {
        const todos = await WorkStreamTodoManager.listTodosForUser(
          params.organisationId,
          userKey
        );
        const open = todos.filter(
          (todo) =>
            todo.livingWageReservationId === params.reservationId &&
            todo.source === 'living_wage' &&
            !todo.completed
        );
        await Promise.all(
          open.map((todo) =>
            WorkStreamTodoManager.updateTodo(todo.id, { completed: true })
          )
        );
      })
    );
  },

  /**
   * Remove incomplete mirrored to-dos when a reservation is cancelled or rejected.
   */
  async deleteIncompleteMirroredTodosForReservation(params: {
    organisationId: string;
    reservationId: string;
    userKeys: string[];
  }): Promise<void> {
    const uniqueKeys = [...new Set(params.userKeys.filter(Boolean))];
    await Promise.all(
      uniqueKeys.map(async (userKey) => {
        const todos = await WorkStreamTodoManager.listTodosForUser(
          params.organisationId,
          userKey
        );
        const open = todos.filter(
          (todo) =>
            todo.livingWageReservationId === params.reservationId &&
            todo.source === 'living_wage' &&
            !todo.completed
        );
        await Promise.all(
          open.map((todo) => WorkStreamTodoManager.deleteTodo(todo.id))
        );
      })
    );
  },
};
